/**
 * Typed edited-field enum and single mapping to DB column names.
 * Used for strict payload allowlist (Step 1) so we never accept arbitrary UI strings.
 * Every _editedFields entry must be a known key; unknown keys throw UNKNOWN_EDITED_FIELD.
 */

/** Domain-layer enum of field names that can be "edited" and sent in an event line update. Use these when building _editedFields. */
export const EVENT_LINE_EDITED_FIELDS = [
  'qty',
  'description',
  'charge_days',
  'unit_price',
  'discount',
  'section_id',
  'line_number',
  'action',
  'kind',
  'product_id',
  'item_id',
  'not_discountable',
  'non_printable',
  'section_show_subtotal',
  'room_key',
  'usage_room_date',
  'event_room_access_id',
  'fill_date',
  'fill_time',
  'return_date',
  'return_time',
  'kit_anchor',
  'kit_component',
  'kit_instance_id',
  'kit_item_id',
  'kit_component_id',
  'kit_parent_event_line_id',
  'kit_label',
  'kit_pricing_mode',
  'note',
  'quick_turn',
  'name',
  'custom_name',
] as const;

export type EventLineEditedField = (typeof EVENT_LINE_EDITED_FIELDS)[number];

/** UI/Row property names that are aliases for a DB column. Map these to the single DB column name. */
const UI_ALIAS_TO_DB: Record<string, string> = {
  line_order: 'line_number',
  price: 'unit_price',
  days: 'charge_days',
  room: 'event_room_access_id',
};

/** Set of all valid DB column names that may appear in an event_lines update payload for "edited" fields. */
const DB_COLUMNS_FOR_EDITS = new Set<string>([
  ...EVENT_LINE_EDITED_FIELDS,
  ...Object.values(UI_ALIAS_TO_DB),
]);

/** All allowed keys (UI aliases + DB names) that can be passed as _editedFields. */
const KNOWN_EDITED_KEYS = new Set<string>([
  ...EVENT_LINE_EDITED_FIELDS,
  ...Object.keys(UI_ALIAS_TO_DB),
]);

/**
 * Map edited-field keys (UI or domain) to DB column names in exactly one place.
 * Unknown keys throw with code UNKNOWN_EDITED_FIELD.
 */
export function editedFieldsToDbColumns(editedFields: string[]): string[] {
  const dbColumns: string[] = [];
  for (const key of editedFields) {
    const dbCol = UI_ALIAS_TO_DB[key] ?? (KNOWN_EDITED_KEYS.has(key) ? key : null);
    if (dbCol == null) {
      const err = new Error(
        `[editedFields] Unknown edited field: "${key}". Allowed: ${[...KNOWN_EDITED_KEYS].sort().join(', ')}.`
      ) as Error & { code?: string };
      err.code = 'UNKNOWN_EDITED_FIELD';
      throw err;
    }
    dbColumns.push(dbCol);
  }
  return [...new Set(dbColumns)];
}

export function isKnownEditedField(key: string): boolean {
  return KNOWN_EDITED_KEYS.has(key);
}
